import { useDispatch, useSelector } from "react-redux";

import { Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, FormGroup, IconButton, Link, ListItemText, MenuItem, Paper, Select, Switch, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";

import { AccessTime, Add, AddComment, Analytics, Cancel, CheckBoxOutlineBlank, Sd, HighQuality, InsertChart, ChevronLeft, Comment, FileDownload, FlashOn, Fullscreen, GridView, LegendToggle, Pause, PictureAsPdf, PlayArrow, QueryStats, Refresh, Save, Stop, Edit } from "@mui/icons-material";

import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import { selectRTFO, setCurrentStageName, setIsLoadingData, setPadData, setStageData, setStagesData, deleteStagesData, setSeismicityData } from "../../reducers/rtfoSlice";

import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { RTFOPumpingChart } from "./RTFOPumpingChart";
import { RTFOPressureChart } from "./RTFOPressureChart";
import { RTFOHistoricChart } from "./RTFOHistoricChart";
import { RTFOMonitorChart } from "./RTFOMonitorChart";
import { RTFOSeismicAmplitudeChart } from "./RTFOSeismicAmplitudeChart";

import { selectUser } from "../../reducers/userSlice";
import { RTFODerivativeChart } from "./RTFODerivativeChart";
import { RTFOISIPChart } from "./RTFOISIPChart";
import { RTFOISIP } from "./RTFOISIP";

import { useSocket } from "../../context/useSocket";
import { RTFOStepdownChart } from "./RTFOStepdownChart";

import { RTFOMetrics } from "./RTFOMetrics";
import { RTFOStepdown } from "./RTFOStepdown";
import { RTFOMetricsPanel } from "./RTFOMetricsPanel";
import RTFOMapWithWellbore from "./RTFOMapWithWellbore";
import { useSnackbar } from "notistack";
import { RTFOEditComment } from "./RTFOEditComment";
import { RTFOFracModel } from "./RTFOFracModel";
import { RTFOPressureAnalysis } from "./RTFOPressureAnalysis";
import { RTFOFBA } from "./RTFOFBA";
import { RTFOSeismicity } from "./RTFOSeismicity";
import { RTFOLoadingPadData } from "./RTFOLoadingPadData";
import { RTFOSettings } from "./RTFOSettings";

import { RTFODataAnalytics } from "./RTFODataAnalytics";
import RTFOFracSimulator from "./RTFOFracSimulator";
import { RTFOSeismicAmplitudeTable } from "./RTFOSeismicAmplitudeTable";
import RTFOSeismicReportMap from "./RTFOSeismicReportMap";
import { getRTFOClientName, getRTFOCompanyName, isRTFOAdminUser } from "./rtfoConfig";
import { RTFOPumpdownChart } from "./RTFOPumpdownChart";
import { RTFOReprocessStage } from "./RTFOReprocessStage";
import axios from "axios";
import RTFOCommandCenter from "./RTFOCommandCenter";
import RTFOUploadData from "./RTFOUploadData";
import { RTFOHistoricPumpdownChart } from "./RTFOHistoricPumpdownChart";
import { rtfoConversionFactors } from "./rtfoConversionFactors";
import { RTFOFracfeedSimulatorChart } from "./RTFOFracfeedSimulatorChart";

const useStyles = makeStyles(theme => ({
  root: {
    color: 'text.primary',
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    padding: 0,
    marginTop: 20,
    overflowY: 'scroll',
    overflowX: 'hidden',
    position: 'relative'
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: '49% 49%',
    gridRow: 'auto auto',
    gridColumnGap: '20px',
    gridRowGap: '20px',
  }
}));

const defaultChartTemplate = {
  'Default': [
    'Treating Pressure (MPa)', 
    'Tubing Pressure (MPa)', 
    'Slurry Rate (m3/min)', 
    'Propp. Surf. (kg/m3)', 
    'Propp. BH (kg/m3)', 
    'Cum. Propp. (ton)', 
    'Cum. Propp. BH (ton)', 
    'DFR Conc. (kg/m3)', 
    'LFR Conc. (L/m3)', 
    'Fracfeed', 
    'Fracfeed Slope',
    'TP Rate (L/min)',
    'BHP Top (MPa)',
    'BHP Bottom (MPa)',
    'BHT Top (degC)',
    'BHT Bottom (degC)',
    'Cross-Linker Conc. (L/m3)',
    'Gellant Conc. (L/m3)'
  ]
};

export const RTFO = ({ currentWellName, setCurrentWellName }) => {

  const theme = useTheme();
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { socket } = useSocket();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const userEmail = user?.value?.email;
  const darkMode = user?.value?.darkMode;

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStageName = currentRTFO?.currentStageName;
  const stageData = currentRTFO?.stageData?.[currentStageName];

  const [tabItem, setTabItem] = useState(0);
  const [isDownloadAllStages, setIsDownloadAllStages] = useState(userEmail === 'bhepburn@wcap.ca' || userEmail === 'wcap@ssdynamics.com' || userEmail === 'bheppie@shaw.ca');

  const [liveMonitoring, setLiveMonitoring] = useState(false);
  const [isMonitoringPaused, setIsMonitoringPaused] = useState(false);
  const [gridView, setGridView] = useState(true);

  const [reprocess, setReprocess] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [chartTemplates, setChartTemplates] = useState(user?.value?.rtfoChartTemplates || defaultChartTemplate);
  const [selectedTemplate, setSelectedTemplate] = useState('Default');

  const [monitorData, setMonitorData] = useState(null);
  const [strainData, setStrainData] = useState(null);
  const [isLoadingStrainData, setIsLoadingStrainData] = useState(true);
  const [editStageVisible, setEditStageVisible] = useState(false);

  const allMonitorWells = currentRTFO?.monitorIds;

  // const isLoadingStrainData = useRef(true);
  const isLoadingMonitorData = useRef(false);

  const [allPadsMetrics, setAllPadsMetrics] = useState(null);
  const [isLoadingPadsMetrics, setIsLoadingPadsMetrics] = useState(true);
  const [seismicReportMapVisible, setSeismicReportMapVisible] = useState(false);
  const [seismicReportData, setSeismicReportData] = useState(null);
  const [seismicReportMap, setSeismicReportMap] = useState(null);

  const [reprocessStageVisible, setReprocessStageVisible] = useState(false);

  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(null);


  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);
  
      if (!response) return;
  
      if (response.messageType === 'padData') {
        const padData = response?.payload;
        
        if (padData) {
          dispatch(setPadData(padData));

          // request seismicity data
          socket?.send(JSON.stringify({
            messageType: 'getSeismicityData',
            padName: padData?.name
          }));
        };    
        
        setIsInitialLoad(false);
        
      } else if (response.messageType === 'stageData') {
        const stageData = response?.payload?.stageData;
        const seismicityData = response?.payload?.seismicityData;
        const newEvents = response?.payload?.newEvents;
        const seismicityOnly = response?.payload?.seismicityOnly;
        const initialLoad = response?.payload?.initialLoad;
        const highResolutionData = response?.payload?.highResolutionData;
        const incrementalData = response?.payload?.incrementalData;
        const recalculatedData = response?.payload?.recalculatedData;
        const showSnackbar = response?.payload?.showSnackbar;

        dispatch(setIsLoadingData(false));

        const updateData = !lastUpdateTimestamp || new Date()?.getTime() > new Date(lastUpdateTimestamp)?.getTime() + 30 * 1000;

        setIsInitialLoad(false);

        if (!liveMonitoring) {
          socket?.send(JSON.stringify({
            messageType: 'stopPadMonitoring',
            padName: currentRTFO?.name,
            userEmail
          }));
        };

        // If monitoring is paused, skip
        if (isMonitoringPaused && !updateData && !stageData?.seismicOnly) return;

        if ((stageData && stageData?.name) || seismicityOnly) {
          console.log('Updating stage data...');

          // Check if new stage is detected and request last 5 stages
          if (stageData?.name && currentRTFO?.stages?.length > 1 && !currentRTFO?.stages?.includes(stageData?.name)) {
            console.log('New stage detected! Requesting last 5 stages');

            const last5Stages = currentRTFO?.stages?.slice(-5);
            last5Stages?.length > 0 && socket?.send(JSON.stringify({
              messageType: 'getResampledStagesData',
              stageNames: last5Stages,
              userEmail: userEmail
            }));
          };

          dispatch(setStageData({stageData, seismicityData, newEvents, seismicityOnly, initialLoad, highResolutionData, liveMonitoring, incrementalData}));
          setLastUpdateTimestamp(new Date());
        };
  
        if (initialLoad && stageData?.name === currentRTFO?.stages?.[currentRTFO?.stages?.length - 1] && !highResolutionData && !liveMonitoring) {
          closeSnackbar();
          enqueueSnackbar(`All stages for pad ${currentRTFO?.name} downloaded successfully`, {
            variant: 'success'
          });
          setIsInitialLoad(false);
        };

        // If admin client, send a process request
        if (reprocess && liveMonitoring && incrementalData) {
          const timeout = (!stageData || stageData?.seismicOnly || seismicityOnly) ? 30000 : 3000;
          setTimeout(() => {
            console.log(`Sending stage process request for pad ${currentRTFO?.name}...`);
            dispatch(setIsLoadingData(true));
            socket?.send(JSON.stringify({ // send latest stage process request
              messageType: 'processStage',
              padName: currentRTFO?.name,
              userEmail: userEmail
            }));
          }, timeout);
        };
  

        if (showSnackbar && stageData?.name) {
          enqueueSnackbar(`Updated stage ${stageData?.name} successfully`, {
            variant: 'success'
          });
        }

        if (recalculatedData) closeSnackbar();
  
      } else if (response.messageType === 'stagesData') {
        const stagesData = response?.payload?.stagesData;
        const initialLoad = response?.payload?.initialLoad;

        if (initialLoad || isDownloadAllStages) {
          dispatch(setIsLoadingData(false));
        };

        setIsInitialLoad(false);

        dispatch(setStagesData(stagesData));
        closeSnackbar();

        if (isDownloadAllStages && response?.payload?.isDownloadAllStages) {
          enqueueSnackbar(`All stages for pad ${currentRTFO?.name} downloaded successfully`, {
            variant: 'success'
          });
        };
  
      } else if (response.messageType === 'padSaved') {
  
        const payload = response?.payload;
        if (payload?.success) {
          enqueueSnackbar(`Pad ${payload?.padName} saved successfully`, { variant: 'success' });
        } else {
          enqueueSnackbar(`Error saving pad ${payload?.padName}`, { variant: 'error' });
        };
      } else if (response.messageType === 'stageSaved') {
  
        const payload = response?.payload;
        if (payload?.success) {
          enqueueSnackbar(`Stage ${payload?.stageName} saved successfully`, { variant: 'success' });
          socket?.send(JSON.stringify({ // request high-res data for the saved stage
            messageType: 'getStageData',
            stageName: payload?.stageName,
            userEmail: userEmail
          }));
        } else {
          enqueueSnackbar(`Error saving stage ${payload?.stageName}`, { variant: 'error' });
        };
      } else if (response.messageType === 'padMetrics') {
        const padMetrics = response?.payload?.padMetrics;
        const allPads = response?.payload?.allPads || [];
        const padName = Object.keys(padMetrics)?.[0];

        if (padMetrics && padName) {
          const newAllPadsMetrics = allPadsMetrics ? {...allPadsMetrics} : {};

          for (const pad of allPads) {
            if (!newAllPadsMetrics?.[pad.name]?.metrics) {
              newAllPadsMetrics[pad.name] = pad;
            };
          };

          newAllPadsMetrics[padName] = padMetrics[padName];

          console.log(newAllPadsMetrics)

          setAllPadsMetrics(newAllPadsMetrics);
          setIsLoadingPadsMetrics(false);
        };  
      } else if (response.messageType === 'allPadsMetrics') {
        const allPadsMetrics = response?.payload;
        
        if (allPadsMetrics) {
          setAllPadsMetrics(allPadsMetrics);
          setIsLoadingPadsMetrics(false);
        };  
      } else if (response.messageType === 'seismicReportData') {
        const seismicReportResponse = response?.payload?.seismicReportData;
        if (seismicReportResponse?.length > 0) {
          setSeismicReportData(seismicReportResponse);
          setSeismicReportMapVisible(true);
        };
      } else if (response.messageType === 'monitorData') {
        isLoadingMonitorData.current = false;

        const monitorWellNames = response.payload ? Object.keys(response.payload) : [];

        for (const monitorWellName of monitorWellNames) {
          const monitorWellData = response.payload?.[monitorWellName];
          setMonitorData(prev => ({...prev, [monitorWellName]: monitorWellData}));
        };
      } else if (response.messageType === 'seismicityData') {

        const seismicityData = response.payload.seismicityData;
        const isPartialData = response.payload.isPartialData;
        
        if (seismicityData?.length > 0) {
          dispatch(setSeismicityData({ seismicityData, isPartialData }));
        };
      };
    };
  };

  const stageNames = useMemo(() => {
    let names = [...currentRTFO?.stages]?.reverse();

    if (currentWellName !== 'All') {
      names = names.filter(name => name.includes(currentWellName));
    }; 
    
    return names;
  }, [currentRTFO?.stages, currentWellName]);

  const firstRender = useRef(true);

  // automatically start monitoring if pad is live
  useEffect(() => {

    if (userEmail === 'aborisov@ssdynamics.com') return;

    console.log('Attempting to start live monitoring...');
    if (currentRTFO?.isActive && !liveMonitoring && socket?.readyState === 1 && firstRender.current) {
      console.log('Starting live monitoring...');
      firstRender.current = false;
      setReprocess(false);
      setLiveMonitoring(true);
      dispatch(setIsLoadingData(true));
      socket?.send(JSON.stringify({
        messageType: 'startPadMonitoring',
        padName: currentRTFO?.name,
        userEmail
      }));
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // restart monitoring on websocket reconnect
  useEffect(() => {
    const onConnect = () => {
      if (currentRTFO?.isActive && !liveMonitoring && process.env.REACT_APP_NODE_ENV !== 'local') {
        console.log('Automatically restarting live monitoring...');
        setReprocess(false);
        dispatch(setIsLoadingData(false));
        handleStartClick();
      };
    };

    const onDisconnect = () => {
      setReprocess(false);
      setLiveMonitoring(false);
      dispatch(setIsLoadingData(false));
    };

    socket?.addEventListener('open', onConnect);
    socket?.addEventListener('close', onDisconnect);

    return () => {
      socket?.removeEventListener('open', onConnect);
      socket?.removeEventListener('close', onDisconnect);
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket])

  // fetch current stage data
  useEffect(() => {

    if (socket?.readyState !== 1) return;

    // request high resolution data for the current stage if not available (only in monitor, stepdown, frac model, FDI and seismicity tabs)
    if (
      (tabItem === 0 || tabItem === 1 || tabItem === 2 || tabItem === 3 || tabItem === 4 || tabItem === 5) && 
      currentRTFO?.stageData?.[currentStageName] &&
      (!currentRTFO?.stageData?.[currentStageName]?.highResolutionData)
      ) {
      const currentStageIndex = stageNames?.indexOf(currentStageName);

      dispatch(setIsLoadingData(true));

      // Find next 5 and prev 5 stages from the new stage that need resampled data
      const next5StageNames = stageNames?.slice(Math.max(currentStageIndex - 5, 0), currentStageIndex);
      const prev5StageNames = stageNames?.slice(currentStageIndex + 1, Math.min(currentStageIndex + 6, stageNames?.length));
      let neighboringStageNames = next5StageNames?.concat(prev5StageNames);
      let requestedStageNames = neighboringStageNames?.filter(stageName => (!currentRTFO?.stageData?.[stageName]?.coldboreData || currentRTFO?.stageData?.[stageName]?.highResolutionData));

      if (liveMonitoring) {
        requestedStageNames = requestedStageNames?.filter(stageName => stageName !== stageNames?.[0]);
      };

      // Request low-res data for the previous stages
      socket?.send(JSON.stringify({
        messageType: 'getResampledStagesData',
        stageNames: requestedStageNames,
        userEmail: userEmail
      }));

      // Request high-res data for the new stage
      console.log('Requesting high-resolution data for stage', currentStageName);
      socket?.send(JSON.stringify({
        messageType: 'getStageData',
        stageName: currentStageName,
        userEmail: userEmail
      }));

      // Delete stage data and monitor data for all stages that are not within +/- 5 from the current stage
      const stagesToKeep = [...neighboringStageNames, currentStageName];
      
      if (liveMonitoring) {
        stagesToKeep.push(stageNames?.[0]);
      };

      if (!isDownloadAllStages) {
        dispatch(deleteStagesData(stagesToKeep)); // delete stage data for all stages not in this list
      };

    } else { // check if there any other stages other than the current stage that have high resolution data
      const requestedStageNames = [];

      const stagesToCheck = stageNames?.filter(s => s !== currentStageName && s !== stageNames?.[0]);
      // const stagesToCheck = liveMonitoring ? stageNames?.filter(s => s !== currentStageName) : stageNames?.filter(s => s !== currentStageName && s !== stageNames?.[0]);

      for (const stageName of stagesToCheck) {
        if (currentRTFO?.stageData?.[stageName]?.highResolutionData) {
          requestedStageNames.push(stageName);
        };
      };

      if (requestedStageNames?.length > 0) {
        console.log(`Requesting low-resolution data for ${requestedStageNames?.length} stages`);
        socket?.send(JSON.stringify({
          messageType: 'getResampledStagesData',
          stageNames: requestedStageNames,
          userEmail: userEmail
        }));
      };
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStageName, socket, tabItem, userEmail]);

  // When a new stage name is detected, reset well filter
  useEffect(() => {

    if (currentWellName !== 'All' && !currentStageName?.includes(currentWellName)) {
      setCurrentWellName('All');
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStageName]);
  
  // Pause live monitoring if on map or simulator tab
  useEffect(() => {
    if (!liveMonitoring || (reprocess && isRTFOAdminUser(userEmail))) return;

    if ((tabItem === 6 || tabItem === 9)) {
      setIsMonitoringPaused(true);
    } else if (isMonitoringPaused) {
      setIsMonitoringPaused(false);
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabItem]);

  // dispatch seismic report request
  useEffect(() => {
    if (seismicReportMap && socket && socket.readyState === 1) {
      socket?.send(JSON.stringify({ // send seismic report process request
        messageType: 'dispatchSeismicReport',
        padName: currentRTFO?.name,
        client: currentRTFO?.client,
        seismicReportData: seismicReportData || [],
        seismicReportMap: seismicReportMap
      }));
    };
    
    setSeismicReportMap(null);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seismicReportMap]);

  // fetch strain data
  useEffect(() => {
    if (!currentStageName || isLoadingStrainData) return;

    console.log('Fetching strain data for stage', currentStageName);

    setIsLoadingStrainData(true);

    const timestamp = new Date().getTime();
  
    axios.get(`https://ssd-straindata.s3.us-east-2.amazonaws.com/${currentStageName}_strain.json?timestamp=${timestamp}`)
    .then(res => {
      setStrainData(res.data);
      setIsLoadingStrainData(false);
    })
    .catch(err => {
      setIsLoadingStrainData(false);
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStageName]);

  // MEMOIZE CHART COMPONENTS
  const rtfoMonitor = useMemo(() => {
    return (
      <RTFOMonitor 
        currentRTFO={currentRTFO}
        liveMonitoring={liveMonitoring}
        darkMode={darkMode}
        gridView={gridView}
        theme={theme}
        stageData={stageData}
        currentWellName={currentWellName}
        stageNames={stageNames}
        socket={socket}
        userEmail={userEmail}
        dispatch={dispatch}
        chartTemplates={chartTemplates}
        selectedTemplate={selectedTemplate}
        isDownloadAllStages={isDownloadAllStages}
      />
    );
  }, [currentRTFO, liveMonitoring, darkMode, gridView, theme, stageData, currentWellName, stageNames, socket, userEmail, dispatch, chartTemplates, selectedTemplate, isDownloadAllStages]);

  const rtfoStepdown = useMemo(() => {
    return (
      <RTFOStepdown 
        currentRTFO={currentRTFO}
        darkMode={darkMode}
        gridView={gridView}
        user={user}
        chartTemplates={chartTemplates}
        setChartTemplates={setChartTemplates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        defaultChartTemplate={defaultChartTemplate}
        isDownloadAllStages={isDownloadAllStages}
        setIsDownloadAllStages={setIsDownloadAllStages}
      />
    );
  }, [chartTemplates, currentRTFO, darkMode, gridView, isDownloadAllStages, selectedTemplate, user]);

  const rtfoIsip = useMemo(() => {
    return (
      <RTFOISIP
        theme={theme}
        currentRTFO={currentRTFO} 
        darkMode={darkMode}
        currentWellName={currentWellName}
        isDownloadAllStages={isDownloadAllStages}
      />
    );
  }, [currentRTFO, currentWellName, darkMode, isDownloadAllStages, theme]);
   
  try {
    return (
      <div className={classes.root} id="rtfo" style={{backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light']}}>
        {!user?.value?.rtfoAccess ?
        <div style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          Please add RTFO subscription to your account
        </div> :
        isInitialLoad ? 
        <RTFOLoadingPadData setIsInitialLoad={setIsInitialLoad} darkMode={user?.value?.darkMode} /> 
        :
        <>
          <Box 
            className={classes.header} 
            style={{boxShadow: `0px 2px 6px ${darkMode ? '#222' : 'lightgrey'}`, backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light}}
          >
            <div style={{width: 190, display: 'flex', alignItems: 'center', margin: 0, cursor: 'pointer'}} onClick={handleBackToPadActivity}>
              <IconButton color="inherit" style={{margin: 0}}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" color="textPrimary">Back to pad activity</Typography>
            </div>

            <div style={{ position: 'absolute', top: 10, right: 20}}>
              <Link href="https://ssdprojects.s3.us-east-2.amazonaws.com/Athena+User+Manual_2.12_RTFO.pdf"
                target="_blank" style={{textDecoration: 'none', height: 28}}>
                <Tooltip title="Download RTFO manual">
                  <Button 
                    style={{height: 28, color: darkMode ? '#fff' : theme.palette.primary.main, borderColor: darkMode ? '#fff' : theme.palette.primary.main, marginTop: 0}} 
                    startIcon={<PictureAsPdf />} 
                    color={darkMode ? "inherit" : "primary"}
                    variant="outlined"
                    size="small"
                  >
                    RTFO User Manual
                  </Button>
                </Tooltip>
              </Link>
            </div>

            <Box style={{display: 'flex', justifyContent: 'space-between', padding: '0 20px'}}>
              <div>
                <Typography variant="h5">
                  {currentRTFO?.name ? `Pad ${currentRTFO.name}` : "No pad selected"}
                </Typography>
                <Typography fontSize="14px" color={"textSecondary"}>
                  {currentRTFO?.currentStageName ? 
                  `Stage: ${currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.stageNumber} | 
                  Well: ${currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.wellName} | 
                  ${currentRTFO?.settings?.units === 'field' ? 'API Number' : 'UWI'}: ${currentRTFO?.name === 'Test' ? 'Test' : (currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.wellUwi || '')} |
                  Pad Phase: ${currentRTFO?.padPhase || ''} |
                  Company: ${getRTFOCompanyName(currentRTFO?.client) || ''} |
                  Formation: ${(currentRTFO?.formation) || ''} |
                  Area: ${(currentRTFO?.area) || ''} |
                  Total Stages: ${currentRTFO?.stages ? currentRTFO?.stages?.length : 0} of ${calculateTotalStages(currentRTFO?.perfSchedule)} |
                  Completed: ${(Math.min((currentRTFO?.stages?.length / calculateTotalStages(currentRTFO?.perfSchedule) * 100), 100) || 0)?.toFixed(1)}%
                ` : "No stage selected"}
                </Typography>

              </div>
              <Box style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end'}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Typography fontSize="14px">
                    Status:
                  </Typography>
                  <Box style={{margin: "0 10px", height: 16, width: 16, borderRadius: '50%', backgroundColor: ((socket?.current?.readyState === 1 || liveMonitoring) ? 'limegreen' : 'tomato')}}></Box>
                  <Typography fontSize="14px">
                    {liveMonitoring ? 'Live Monitoring...' : 'Idle'}
                  </Typography>
                </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  {currentRTFO?.isLoadingData && 
                  <CircularProgress style={{width: 20, height: 20, marginRight: 10}} color="inherit"></CircularProgress>}
                  <AccessTime fontSize="14px" color="textSecondary" style={{marginRight: 5}} />
                  <Typography color="textSecondary" style={{fontSize: '12px', paddingBottom: 5, paddingTop: 5}}>
                    {/* Last Updated: {currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.lastModified || "No stage selected"}  */}
                    Last Updated: {currentRTFO?.lastModified || "No stage selected"} 
                  </Typography>
                </div>
              </Box>
            </Box>
          </Box>
          <div style={{
            width: '100%', 
            display: 'flex', 
            flexDirection: 'row', 
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: (darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light),
            borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
            marginBottom: 0
          }}>
            <Tabs
              value={tabItem}
              textColor={darkMode ? "inherit" : "primary"}
              TabIndicatorProps={{style: {background: (darkMode ? '#fff' : theme?.palette?.primary?.main)}}}
              onChange={(e, value) => setTabItem(value)}
              style={{
                height: 24
              }}
            >
              <Tab label={<Typography fontSize="12px">Monitor</Typography>} key='monitor' />
              <Tab label={<Typography fontSize="12px">ISIP</Typography>} key='isip' />
              <Tab label={<Typography fontSize="12px">Frac Hub</Typography>} key='stepdown' />
              <Tab label={<Typography fontSize="12px">Frac Model</Typography>} key='fracModel' />
              <Tab label={<Typography fontSize="12px">Seismicity</Typography>} key='seismicity' />
              <Tab label={<Typography fontSize="12px">FDI</Typography>} key='pressureAnalysis' onClick={handlePressureAnalysisClick} disabled={!currentRTFO?.perfSchedule || !currentRTFO?.surveys} />
              <Tab label={<Typography fontSize="12px">Map View</Typography>} key='mapView' onClick={handleFetchAllPadsMetrics} />
              <Tab label={<Typography fontSize="12px">Metrics</Typography>} key='metrics' />
              <Tab label={<Typography fontSize="12px">Analytics</Typography>} key='analytics' onClick={handleFetchAllPadsMetrics}/>
              <Tab label={<Typography fontSize="12px">Simulator</Typography>} key='fracSimulator' />
              <Tab label={<Typography fontSize="12px">FBA</Typography>} key='fba' />
              <Tab label={<Typography fontSize="12px">Settings</Typography>} key='settings' />
              <Tab label={<Typography fontSize="12px">Command Center</Typography>} key='commandCenter' disabled={userEmail !== 'aborisov@ssdynamics.com'} />
              {/* <Tab label={<Typography fontSize="12px">Command Center</Typography>} key='commandCenter' onClick={handleFetchContinuousData} /> */}
            </Tabs>

            <FormControlLabel
              control={<Switch />} 
              label={<Typography style={{fontSize: 12}}>Download All Stages</Typography>}
              size="small" 
              checked={isDownloadAllStages} 
              onChange={handleDownloadAllStages}
              style={{marginRight: 20}}
            />

          </div>

          <div style={{boxShadow: liveMonitoring && `inset 0px 0px 15px rgba(7, 88, 183, 1)`, paddingBottom: 40}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 0, padding: '10px 20px 0 20px'}}>
              <div style={{display: 'flex', alignItems: 'center', marginTop: 0}} >
                {userEmail === 'aborisov@ssdynamics.com' &&
                  <>
                    <Button
                    size="small"
                    style={{cursor: !currentRTFO?.isActive && 'not-allowed', backgroundColor: !currentRTFO?.isActive ? 'lightgrey' : ((liveMonitoring && !isMonitoringPaused) ? '#f44336' : (liveMonitoring && isMonitoringPaused) ? 'orange' : '#0758b7'), color: darkMode ? '#000' : '#fff', marginTop: 0, marginBottom: 0, marginRight: 10, height: 28, fontSize: 12}}
                    variant="contained"  
                    startIcon={(liveMonitoring && !isMonitoringPaused) ? <Stop /> : (liveMonitoring && isMonitoringPaused) ? <Pause /> :  <PlayArrow />}
                    onClick={handleStartClick}
                    disabled={(!liveMonitoring && currentRTFO?.isLoadingData) || !currentRTFO?.isActive}
                  >
                    {(liveMonitoring && !isMonitoringPaused) ? 'Stop Monitoring' : (liveMonitoring && isMonitoringPaused) ? 'Paused' : 'Start Monitoring'}
                  </Button>

                  <Button
                    size="small"
                    color={(darkMode ? "inherit" : "primary")}
                    style={{
                      color: (darkMode ? '#fff' : theme.palette.primary.main), 
                      borderColor: (darkMode ? '#fff' : theme.palette.primary.main), 
                      marginRight: 10, 
                      height: 28,
                      fontSize: 12
                    }}
                    variant="outlined"  
                    startIcon={<Refresh />}
                    onClick={handleReloadClick}
                    disabled={liveMonitoring || currentRTFO?.isLoadingData}
                  >
                    Reload Stage
                  </Button>
                </>
                }

                <Button
                  size="small"
                  color={(darkMode ? "inherit" : "primary")}
                  style={{
                    color: (darkMode ? '#fff' : theme.palette.primary.main), 
                    borderColor: (darkMode ? '#fff' : theme.palette.primary.main), 
                    marginRight: 10, 
                    height: 28,
                    fontSize: 12
                  }}
                  variant="outlined"  
                  startIcon={<FileDownload />}
                  onClick={handleDownloadClick}
                  disabled={!currentRTFO?.stageData || !currentRTFO?.stageData?.[currentRTFO?.currentStageName]}
                >
                  Download CSV
                </Button>

                <Button
                  size="small"
                  color={(darkMode ? "inherit" : "primary")}
                  style={{
                    color: (darkMode ? '#fff' : theme.palette.primary.main), 
                    borderColor: (darkMode ? '#fff' : theme.palette.primary.main), 
                    marginRight: 10, 
                    height: 28,
                    fontSize: 12
                  }}
                  variant="outlined"  
                  startIcon={<Edit />}
                  onClick={() => setEditStageVisible(true)}
                >
                  Edit Stage
                </Button>

                {isRTFOAdminUser(userEmail) && <FormGroup style={{marginLeft: 20}}>
                  <FormControlLabel 
                    control={<Switch />} 
                    label={<Typography style={{fontSize: 12}}>Process</Typography>}
                    size="small" 
                    checked={reprocess} 
                    onChange={() => {
                      setReprocess(state => !state);
                    }}
                    disabled={liveMonitoring}
                  />
                </FormGroup>}

              </div>

              <FormControl component="fieldset">
                <ToggleButtonGroup
                  value={gridView ? 'grid' : 'full'}
                  exclusive
                  onChange={() => setGridView(value => !value)}
                  size="small"
                  style={{height: 28}}
                >
                  <ToggleButton value="grid" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")} title="Grid View">
                    <GridView />
                  </ToggleButton>
                  <ToggleButton value="full" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")} title="Full Screen View">
                    <Fullscreen />
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>

              <div style={{display: 'flex', alignItems: 'center'}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Typography style={{marginRight: 10}} fontSize='12px'>Well: </Typography>
                  <FormControl>
                    <Select 
                      value={currentWellName}
                      onChange={(e) => {
                        if (e.target.value !== 'All') {
                          const newStageName = currentRTFO?.stageData ? Object.keys(currentRTFO?.stageData)?.filter(name => name?.includes(e.target.value))?.slice(-1)?.[0] : "";
                          handleChangeStageName(newStageName);
                        } else {
                          const newStageName = currentRTFO?.stageData ? Object.keys(currentRTFO?.stageData)?.slice(-1)?.[0] : "";
                          handleChangeStageName(newStageName);
                        };
                        setCurrentWellName(e.target.value);
                      }}
                      size="small"
                      variant="outlined"
                      style={{width: 125, height: 28, zIndex: 69, fontSize: '14px'}}
                    >
                      {['All'].concat([...new Set(currentRTFO?.stages?.map(stageName => stageName?.split('_')?.[1]))]).map((well) => {
                        return (
                          <MenuItem value={well} key={well}>{well}</MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>

                <div style={{display: 'flex', alignItems: 'center', marginLeft: 20}}>
                  <Typography fontSize='12px' style={{marginRight: 10}}>Stage: </Typography>
                  <FormControl>
                    <Select 
                      value={currentRTFO?.currentStageName || ""}
                      onChange={(e) => {
                        handleChangeStageName(e.target.value);
                      }}
                      size="small"
                      variant="outlined"
                      style={{width: 180, height: 28, zIndex: 69, fontSize: '14px'}}
                      disabled={currentRTFO?.isLoadingData}
                    >
                      {stageNames?.map((stage) => {
                        return (
                          <MenuItem value={stage} key={stage} style={{color: currentRTFO?.stageData?.[stage]?.highResolutionData ? (darkMode ? 'white' : theme?.palette?.primary?.main) : currentRTFO?.stageData?.[stage]?.coldboreData ? (darkMode ? 'white' : 'black') : 'lightgrey'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                              {currentRTFO?.stageData?.[stage]?.highResolutionData ? <HighQuality fontSize="small" /> : currentRTFO?.stageData?.[stage]?.coldboreData ? <Sd fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
                              <Typography style={{marginLeft: 5}}>{stage?.split('_')?.slice(1, )?.join('_')}</Typography>
                            </div>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </div>

            <RTFOMetricsPanel darkMode={darkMode} currentStage={stageData} perfSchedule={currentRTFO?.perfSchedule} settings={currentRTFO?.settings} />

            {
            tabItem === 0 ? rtfoMonitor : 
            tabItem === 1 ? rtfoIsip : 
            tabItem === 2 ? rtfoStepdown : 
            tabItem === 3 ? <RTFOFracModel currentWellName={currentWellName} /> : 
            tabItem === 4 ? <RTFOSeismicity darkMode={darkMode} currentRTFO={currentRTFO} currentWellName={currentWellName} /> : 
            tabItem === 5 ? <RTFOPressureAnalysis currentWellName={currentWellName} strainData={strainData} setStrainData={setStrainData} monitorData={monitorData} isLoadingMonitorData={isLoadingMonitorData.current} isLoadingStrainData={isLoadingStrainData} gridView={gridView} theme={theme} darkMode={darkMode} /> : 
            tabItem === 6 ? <RTFOMapWithWellbore selectedWellName={currentWellName} allPadsMetrics={allPadsMetrics} /> : 
            tabItem === 7 ? <RTFOMetrics darkMode={darkMode} currentRTFO={currentRTFO} currentWellName={currentWellName} gridView={gridView} /> : 
            tabItem === 8 ? <RTFODataAnalytics 
              darkMode={darkMode} 
              currentRTFO={currentRTFO} 
              allPadsMetrics={allPadsMetrics} 
              setAllPadsMetrics={setAllPadsMetrics}
              isLoadingPadsMetrics={isLoadingPadsMetrics}
              setIsLoadingPadsMetrics={setIsLoadingPadsMetrics}
              currentWellName={currentWellName} 
              userEmail={userEmail}
            /> : 
            tabItem === 9 ? <RTFOFracSimulator /> : 
            tabItem === 10 ? <RTFOFBA currentWellName={currentWellName === 'All' ? currentStageName?.split('_')?.[1] : currentWellName} /> : 
            tabItem === 11 ? <RTFOSettings currentRTFO={currentRTFO} darkMode={darkMode} userEmail={userEmail} /> :
            <RTFOCommandCenter currentRTFO={currentRTFO} theme={theme} darkMode={darkMode} />
          }
          </div>    

          {seismicReportMapVisible &&  <RTFOSeismicReportMap seismicReportData={seismicReportData} setSeismicReportMapVisible={setSeismicReportMapVisible} setSeismicReportMap={setSeismicReportMap} socket={socket} />}

          {reprocessStageVisible && <RTFOReprocessStage currentRTFO={currentRTFO} reprocessStageVisible={reprocessStageVisible} setReprocessStageVisible={setReprocessStageVisible} darkMode={darkMode} />}
        </>}

        {editStageVisible && <RTFOUploadData currentRTFO={currentRTFO} darkMode={darkMode} setEditStageVisible={setEditStageVisible} />}

      </div>
    );

    function handleDownloadClick() {
      try {
      
        console.log('Downloading stage data CSV...');

        const zip = new JSZip();
        const units = currentRTFO?.settings?.units || 'metric';
        const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
        const currentStageData = currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.coldboreData;
        const currentMonitorData = currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.monitorData;
        const monitorWells = currentMonitorData ? Object.keys(currentMonitorData) : [];

        if (!currentStageData) return;

        const pumpingHeaders = [
          'Pad', 
          'Well', 
          'Stage', 
          'Date', 
          'Time (min)', 
          `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`, 
          'Slurry Rate\'', 
          `Clean Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`, 
          `Pressure (${units === 'field' ? 'psi' : 'MPa'})`, 
          'Pressure\'', 
          'Pressure\'\'',
          `BHP Top (${units === 'field' ? 'psi' : 'MPa'})`,
          `BHP Bottom (${units === 'field' ? 'psi' : 'MPa'})`,
          'Fracfeed',
          `Proppant Surf. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`, 
          `Proppant BH (${units === 'field' ? 'lb/gal' : 'kg/m3'})`, 
          `DFR (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          `LFR (${units === 'field' ? 'gal/mgal' : 'L/m3'})`
        ];

        let pumpingCSV = "";
        pumpingCSV += pumpingHeaders.join(",") + "\r\n";

        const timezoneOffset = new Date()?.getTimezoneOffset() * 60 * 1000;

        currentStageData?.forEach((d, i) => {
          const rnp = d.rnp || 0;
          let row = '';
          row += `${currentRTFO?.name},`;
          row += `${currentStage?.wellName},`;
          row += `${currentStage?.stageNumber},`;
          row += `${new Date(d.timestamp - timezoneOffset)?.toISOString()},`;
          row += `${d.time},`;
          row += `${(d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1)},`;
          row += `${d.ratePrime || 0},`;
          row += `${(d.clean || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1)},`;
          row += `${(d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1)},`;
          row += `${d.pressurePrime || 0},`;
          row += `${d.pressurePrimePrime || 0},`;
          row += `${(d.dhPressureTop || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1)},`;
          row += `${(d.dhPressureBottom || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1)},`;
          row += `${(rnp || 0) * (units === 'field' ? rtfoConversionFactors.rnp : 1)},`;
          row += `${(d.proppant || 0) * (units === 'field' ? rtfoConversionFactors.proppant : 1)},`;
          row += `${(d.proppantBH || 0) * (units === 'field' ? rtfoConversionFactors.proppant : 1)},`;
          row += `${(d.fr || 0) * (units === 'field' ? rtfoConversionFactors.fr : 1)},`;
          row += `${(d.fr2 || 0) * (units === 'field' ? rtfoConversionFactors.fr2 : 1)}`;
          row += "\r\n";
          pumpingCSV += row;
        });


        const monitorHeaders = [
          'Pad', 
          'Well', 
          'Stage', 
          'Monitor Well', 
          'Date', 
          'Time (min)', 
          `Casing Pressure (${units === 'field' ? 'psi' : 'MPa'})`, 
          `Tubing Pressure (${units === 'field' ? 'psi' : 'MPa'})`, 
          'Pressure\'', 
          'Pressure\'\''
        ];

        let monitorCSV = "";
        monitorCSV += monitorHeaders.join(",") + "\r\n";

        for (const monitorWell of monitorWells) {
          for (const d of currentMonitorData?.[monitorWell]) {
            let row = '';
            row += `${currentRTFO?.name}`;
            row += `${currentStage?.wellName}`;
            row += `${currentStage?.stageNumber}`;
            row += `${monitorWell}`;
            row += `${new Date(d.timestamp)?.toISOString()}`;
            row += `${d.time}`;
            row += `${d.casingPressure || 0}`;
            row += `${d.tubingPressure || 0}`;
            row += `${d.pressurePrime || 0}`;
            row += `${d.pressurePrimePrime || 0}`;
            row += "\r\n";
            monitorCSV += row;
          };
        };

        const exportDate = new Date()?.toISOString()?.split('T')?.[0];
        zip.file(`${currentRTFO?.currentStageName}_pumping_data_${exportDate}.csv`, pumpingCSV);
        zip.file(`${currentRTFO?.currentStageName}_monitor_data_${exportDate}.csv`, monitorCSV);

        zip.generateAsync({type: "blob"}).then(content => {
          saveAs(content, `${currentRTFO?.currentStageName}-${exportDate}.zip`);
        });

        return;

      } catch (error) {
        console.log(error);
      };
    };

    function handleReloadClick() {
      try {
        if (isRTFOAdminUser(userEmail) && reprocess) {
          socket?.send(JSON.stringify({
            messageType: 'processStage',
            padName: currentRTFO?.name,
            stageName: currentRTFO?.currentStageName,
            userEmail: userEmail,
            client: currentRTFO?.client
          }));
          // socket?.send(JSON.stringify({
          //   messageType: 'consolidatePadData',
          //   padName: currentRTFO?.name
          // }));
        } else {
          socket?.send(JSON.stringify({
            messageType: 'getStageData',
            stageName: currentRTFO?.currentStageName,
            userEmail: userEmail
          }));
        };
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handleBackToPadActivity() {
      socket?.send(JSON.stringify({
        messageType: 'stopPadMonitoring',
        padName: currentRTFO?.name,
        userEmail
      }));
      setLiveMonitoring(false);
      setReprocess(false);
      dispatch(setPadData(null));
      dispatch(setIsLoadingData(false));
      closeSnackbar();
    };

    function handleChangeStageName(stageName) {
      if (!stageName?.length > 0) return;
      dispatch(setCurrentStageName(stageName));
    };

    function handleFetchAllPadsMetrics() {
      if (socket && socket.readyState === 1 && !allPadsMetrics) {
        let clientName = getRTFOClientName(userEmail);
        if (clientName === 'ssd') {
          clientName = currentRTFO.client;
        };
        socket?.send(JSON.stringify({
          messageType: 'getPadMetrics',
          padName: currentRTFO?.name,
          client: clientName,
          userEmail: userEmail
        }));
        // socket?.send(JSON.stringify({
        //   messageType: 'getAllPadsMetrics',
        //   client: clientName,
        //   userEmail: userEmail
        // }));
      };
    };

    function handlePressureAnalysisClick() {
      
      if (!strainData) {
        console.log('Fetching strain data for stage', currentStageName);

        const timestamp = new Date().getTime();
      
        axios.get(`https://ssd-straindata.s3.us-east-2.amazonaws.com/${currentStageName}_strain.json?timestamp=${timestamp}`)
        .then(res => {
          setStrainData(res.data);
          setIsLoadingStrainData(false);
        })
        .catch(err => {
          setIsLoadingStrainData(false);
        })
      }

      if (socket && socket.readyState === 1 && !monitorData) {
        console.log('Fetching monitor data');

        isLoadingMonitorData.current = true;

        socket?.send(JSON.stringify({
          messageType: 'getMonitorData',
          padName: currentRTFO?.name,
          monitorWellNames: allMonitorWells,
          samplingRate: 300 // in seconds
        }));

        // socket.send(JSON.stringify({
        //   messageType: 'getMonitorData',
        //   padName: currentRTFO?.name,
        //   stageRange: [currentRTFO?.stages?.slice(-6)?.[0], currentRTFO?.stages?.slice(-1)?.[0]],
        //   resample: 300,
        //   userEmail: user?.value?.email
        // }))
      };
    };

    function handleDownloadAllStages() {
      try {
  
        if (isDownloadAllStages) {
          setIsDownloadAllStages(false);
        } else {
          console.log('Sending request for all stages');
    
          if (!socket || socket.readyState !== 1) return;
    
          setIsDownloadAllStages(true);
          dispatch(setIsLoadingData(true));
          enqueueSnackbar(`Loading stage data for pad ${currentRTFO?.name}`, { variant: 'info', persist: true });
    
          socket?.send(JSON.stringify({
            messageType: 'getResampledStagesData',
            stageNames: currentRTFO?.stages,
            isDownloadAllStages: true,
            userEmail: userEmail
          }));
        };
  
      } catch(error) {
        console.log(error);
        return;
      };
    };
  } catch (error) {
    console.log(error);
    return (<div style={{width: '100%'}}></div>);
  };

  function handleStartClick() {
    try {

      setIsMonitoringPaused(false);

      if (liveMonitoring) {
        console.log('Stopping live monitoring');
        socket?.send(JSON.stringify({
          messageType: 'stopPadMonitoring',
          padName: currentRTFO?.name,
          userEmail
        }));
        
        setLiveMonitoring(false);
        setReprocess(false);
        dispatch(setIsLoadingData(false));

      } else {
        console.log('Starting live monitoring...');
        setLiveMonitoring(true);
        dispatch(setIsLoadingData(true));

        if (reprocess) {
          socket?.send(JSON.stringify({
            messageType: 'processStage',
            padName: currentRTFO?.name,
            userEmail: userEmail
          }));
        } else {
          console.log('Sending pad data request...');
          socket?.send(JSON.stringify({
            messageType: 'startPadMonitoring',
            padName: currentRTFO?.name,
            userEmail
          }));
        };
      };

    } catch (error) {
      console.log(error);
      return;
    };
  };
};

function RTFOMonitor({ 
  currentRTFO,
  darkMode,
  gridView,
  theme,
  stageData,
  currentWellName,
  stageNames,
  socket,
  userEmail,
  dispatch,
  chartTemplates, 
  selectedTemplate,
  liveMonitoring,
  isDownloadAllStages,
  pumpdownData
}) {


  return (
    <div style={{padding: '0px 20px 10px 20px', width: '100%'}}>
      <Paper style={{display: 'flex', flexDirection: 'column', padding: 4, backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')])}} elevation={0} sx={{boxShadow: 1}}>
        
        {/* <RTFOStageTimeline currentRTFO={currentRTFO} /> */}

        <div style={{display: 'flex', flexDirection: (gridView ? 'row' : 'column'), marginBottom: 20, width: '100%', position: 'relative'}} >
          <ChartContainer 
            currentRTFO={currentRTFO}
            gridView={gridView}
            darkMode={darkMode}
            theme={theme}
            stageData={stageData}
            name={"Pumping"}
            currentWellName={currentWellName}
            stageNames={stageNames}
            socket={socket}
            userEmail={userEmail}
            dispatch={dispatch}
            chartTemplates={chartTemplates}
            selectedTemplate={selectedTemplate}
            liveMonitoring={liveMonitoring}
            isDownloadAllStages={isDownloadAllStages}
            pumpdownData={pumpdownData}
          />
          {gridView ? <Divider orientation="vertical" flexItem style={{marginTop: 4, marginBottom: -10}} /> : <Divider /> }

          <ChartContainer 
            currentRTFO={currentRTFO}
            gridView={gridView}
            darkMode={darkMode}
            theme={theme}
            stageData={stageData}
            name={"Historic"}
            currentWellName={currentWellName}
            stageNames={stageNames}
            socket={socket}
            userEmail={userEmail}
            dispatch={dispatch}
            chartTemplates={chartTemplates}
            selectedTemplate={selectedTemplate}
            liveMonitoring={liveMonitoring}
            isDownloadAllStages={isDownloadAllStages}
            pumpdownData={pumpdownData}
          />
        </div>
        <Divider />
        <div style={{display: 'flex', flexDirection: (gridView ? 'row' : 'column'), marginBottom: 20}}>
          <ChartContainer 
            currentRTFO={currentRTFO}
            gridView={gridView}
            darkMode={darkMode}
            theme={theme}
            stageData={stageData}
            name={"Derivative"}
            currentWellName={currentWellName}
            stageNames={stageNames}
            socket={socket}
            userEmail={userEmail}
            dispatch={dispatch}
            chartTemplates={chartTemplates}
            selectedTemplate={selectedTemplate}
            liveMonitoring={liveMonitoring}
            isDownloadAllStages={isDownloadAllStages}
            pumpdownData={pumpdownData}
          />
          {gridView ? <Divider orientation="vertical" flexItem style={{marginTop: 4, marginBottom: -10}} /> : <Divider /> }

          <ChartContainer 
            currentRTFO={currentRTFO}
            gridView={gridView}
            darkMode={darkMode}
            theme={theme}
            stageData={stageData}
            name={"Monitor"}
            currentWellName={currentWellName}
            stageNames={stageNames}
            socket={socket}
            userEmail={userEmail}
            dispatch={dispatch}
            chartTemplates={chartTemplates}
            selectedTemplate={selectedTemplate}
            liveMonitoring={liveMonitoring}
            isDownloadAllStages={isDownloadAllStages}
            pumpdownData={pumpdownData}
          />
        </div>
      </Paper>
    </div>
  );

};

function ChartContainer({ 
  currentRTFO,
  gridView, 
  darkMode, 
  theme, 
  stageData, 
  name, 
  currentWellName, 
  stageNames,
  socket,
  userEmail,
  dispatch,
  chartTemplates,
  selectedTemplate,
  liveMonitoring,
  isDownloadAllStages
}) {

  const currentStageName = stageData?.name;

  const [chartName, setChartName] = useState(name || '');
  const [historicStages, setHistoricStages] = useState((currentWellName === 'All' ? stageNames : stageNames?.filter(s => s?.includes(currentWellName))) || []);
  const [selectedStages, setSelectedStages] = useState(((currentWellName === 'All' ? stageNames : stageNames?.filter(s => s?.includes(currentWellName)))?.filter(s => currentRTFO?.stageData?.[s]?.coldboreData)) || []);
  const [chartOverlays, setChartOverlays] = useState(['spikelines', 'legend', 'comments']);
  const [selectedComment, setSelectedComment] = useState(null);
  const [stageComments, setStageComments] = useState(stageData?.comments);
  const [editCommentVisible, setEditCommentVisible] = useState(null);
  const [historicTraces, setHistoricTraces] = useState(['pressure', 'rate', 'fracfeed', 'proppant', 'fr']);
  const [pumpdownHistoryTraces, setPumpdownHistoryTraces] = useState(['pressure', 'rate', 'depth']);
  const [historicChartType, setHistoricChartType] = useState('time');
  const [historicChartNormalizeBy, setHistoricChartNormalizeBy] = useState('proppant');
  const [pumpdownHistoryNormalizeBy, setPumpdownHistoryNormalizeBy] = useState('depth');
  const [editMode, setEditMode] = useState(false);
  const [breakdownTimestamp, setBreakdownTimestamp] = useState(stageData?.metrics?.breakdownTimestamp || null);
  const [breakdownPressure, setBreakdownPressure] = useState(stageData?.metrics?.breakdownPressure || 0);
  const [seismicEditMode, setSeismicEditMode] = useState(false);
  const [seismicAmplitudeEvents, setSeismicAmplitudeEvents] = useState(() => {
    let allAmplitudeEvents = [];

    if (!currentRTFO?.stageData) return [];

    Object.keys(currentRTFO?.stageData)?.forEach(stageName => {
      if (currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents) {
        allAmplitudeEvents.push(currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents);
      };
    });

    allAmplitudeEvents = allAmplitudeEvents.flat(Infinity);
    allAmplitudeEvents = allAmplitudeEvents.sort((a,b) => a.timestamp - b.timestamp);

    return allAmplitudeEvents;
  });
  const [seismicDataType, setSeismicDataType] = useState('realtime');
  const [pumpdownData, setPumpdownData] = useState(false);

  // fetch pumpdown data
  useEffect(() => {
    if (chartName === 'Pumpdown') {
      const timestamp = new Date().getTime();
  
      axios.get(`https://ssd-pumpdowndata.s3.us-east-2.amazonaws.com/${currentStageName}_pumpdown_data.json?timestamp=${timestamp}`)
      .then(res => {
        setPumpdownData(res.data || []);
      })
      .catch(err => {
        console.log(err);
      });
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageData?.name, chartName]);

  useEffect(() => {
    setStageComments(stageData?.comments);

    if (!currentRTFO?.stageData) return;

    let allAmplitudeEvents = [];
    Object.keys(currentRTFO?.stageData)?.forEach(stageName => {
      if (currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents) {
        allAmplitudeEvents.push(currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents);
      };
    });

    allAmplitudeEvents = allAmplitudeEvents.flat(Infinity);
    allAmplitudeEvents = allAmplitudeEvents.sort((a,b) => a.timestamp - b.timestamp);

    setSeismicAmplitudeEvents(allAmplitudeEvents);
  }, [currentRTFO, stageData]);

  // if breakdown changes
  useEffect(() => {
    setBreakdownTimestamp(stageData?.metrics?.breakdownTimestamp || null);
    setBreakdownPressure(stageData?.metrics?.breakdownPressure || 0);
  }, [stageData]);


  const handleStagesChange = (event) => {
    const { target: { value } } = event;

    if (typeof value !== 'string' && value?.indexOf('Deselect All') !== -1) {
      console.log('Deselecting all stages...');
      setSelectedStages([]);
    } else if (typeof value !== 'string' && value?.indexOf('Select All') !== -1) {
      setSelectedStages(stageNames?.filter(s => currentRTFO?.stageData?.[s]?.coldboreData));
    } else {
      const newSelectedStages = typeof value === 'string' ? value.split(',') : value;
      const requestedStages = newSelectedStages?.filter(s => !currentRTFO?.stageData?.[s]?.coldboreData);

      // const stagesToKeep = [...newSelectedStages, currentStageName];
      // if (liveMonitoring) {
      //   stagesToKeep.push(stageNames?.[0]);
      // };

      // if (!isDownloadAllStages) {
      //   dispatch(deleteStagesData(stagesToKeep)); // delete stage data for all stages not in this list
      // }

      if (requestedStages?.length > 0) {
        console.log(`Requesting low-resolution data for ${requestedStages?.length} stages`);
        socket?.send(JSON.stringify({
          messageType: 'getResampledStagesData',
          stageNames: requestedStages,
          userEmail: userEmail
        }));
      };
      setSelectedStages(newSelectedStages);
    };

  };

  const handleHistoricTracesChange = (event) => {
    const { target: { value } } = event;

    const newHistoricTraces = typeof value === 'string' ? value.split(',') : value;
    setHistoricTraces(newHistoricTraces);
  };
  const handlePumpdownHistoryTracesChange = (event) => {
    const { target: { value } } = event;

    const newHistoricTraces = typeof value === 'string' ? value.split(',') : value;
    setPumpdownHistoryTraces(newHistoricTraces);
  };

  const formatTraceName = (name) => {
    return (
      name === 'pressure' ? 'Pressure' :
      name === 'rate' ? 'Rate' :
      name === 'fracfeed' ? 'Fracfeed' :
      name === 'mfr' ? 'MFR' :
      name === 'proppant' ? 'Proppant' :
      name === 'depth' ? 'Depth' :
      'FR'
    );
  }

  useEffect(() => {
    if (name?.length > 0) setChartName(name);
  }, [name]);

  useEffect(() => {
    if (stageNames?.length > 0) {
      let filteredNames = [...stageNames];
      // let filteredNames = stageNames.filter((stageName) => stageName !== currentStageName);

      if (currentWellName !== 'All') {
        filteredNames = filteredNames?.filter(s => s?.includes(currentWellName));
      };
      // filteredNames = filteredNames?.filter(s => currentRTFO?.stageData?.[s]?.coldboreData);

      setHistoricStages(filteredNames);

      setSelectedStages(() => filteredNames?.filter(s => currentRTFO?.stageData?.[s]?.coldboreData));
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageNames, currentWellName]);

  const chartContainerStyle = {
    width: (gridView ? '50%' : '100%'), 
    height: chartName === 'Seismic' ? (gridView ? 1040 : 1400) : (gridView ? 540 : 900), 
    marginBottom: (gridView ? 0 : 20), 
    padding: '40px 4px 4px 4px',
    position: 'relative', 
    backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    outline: (((editMode === 'edit' || editMode === 'presand' || editMode === 'breakdown') && '5px solid #f44336') || (editMode === 'comment' && '5px solid green')),
    zIndex: editMode?.length > 0 && 1000
  };

  function handleChartOverlaysChange(e, newVal) {
    setChartOverlays(newVal);
  };

  return (
    <div style={chartContainerStyle} onKeyDown={handleKeyDown} tabIndex="1">
      <div style={{display: (chartName === 'Historic' || chartName === 'Pumpdown History' || name === 'none') ? 'none' : 'flex', position: 'absolute', top: 10, left: 10}}>
        <ToggleButtonGroup
          value={chartOverlays}
          size="small"
          style={{height: 24}}
          onChange={handleChartOverlaysChange}
        >
          <ToggleButton value={'spikelines'} size="small" title="Show Info on Hover" color={(darkMode ? "" : "primary")}>
            <QueryStats style={{fontSize: '16px'}} />
          </ToggleButton>
          <ToggleButton value={'legend'} size="small" title="Show Legend" color={(darkMode ? "" : "primary")}>
            <LegendToggle style={{fontSize: '16px'}} />
          </ToggleButton>
          {(chartName === 'Pumping' || chartName === 'Pressure') && <ToggleButton value={'comments'} size="small" title="Show Comments" color={(darkMode ? "" : "primary")}>
            <Comment style={{fontSize: '16px'}} />
          </ToggleButton>}
        </ToggleButtonGroup>

        {(chartName === 'Pumping' || chartName === 'Pressure' || chartName === 'Seismic') && <Divider flexItem orientation="vertical" style={{ margin: 5 }} />}

        {(chartName === 'Pumping' || chartName === 'Pressure') && <ToggleButtonGroup
          value={editMode}
          size="small"
          style={{height: 24}}
          onChange={(e, newVal) => setEditMode(newVal)}
          exclusive
        >
          <ToggleButton value={'breakdown'} size="small" title="Edit Breakdown Pressure" color={(darkMode ? "" : "primary")}>
            <FlashOn style={{fontSize: '16px'}} />
          </ToggleButton>
          <ToggleButton value={'comment'} size="small" title="Add a Comment" color={(darkMode ? "" : "primary")}>
            <AddComment style={{fontSize: '16px'}} />
          </ToggleButton>
        </ToggleButtonGroup>}

        {chartName === 'Seismic' &&  
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <ToggleButtonGroup
              value={seismicEditMode}
              size="small"
              style={{height: 24}}
              onChange={(e, newVal) => setSeismicEditMode(newVal)}
              exclusive
            >
              <ToggleButton value={true} size="small" title="Add Seismic Timestamp" color={(darkMode ? "" : "primary")}>
                <Add style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={seismicDataType}
              size="small"
              style={{height: 24}}
              onChange={(e, newVal) => newVal && setSeismicDataType(newVal)}
              exclusive
            >
              <ToggleButton value={'realtime'} size="small" title="All Seismic Events" color={(darkMode ? "" : "primary")}>
                All
              </ToggleButton>
              <ToggleButton value={'reprocessed'} size="small" title="Confirmed Seismic Events" color={(darkMode ? "" : "primary")}>
                Confirmed
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        }
      </div>

      {(chartName === 'Pumping' || chartName === 'Pressure') && editMode && <div 
        style={{
          zIndex: 69, 
          position: 'absolute', 
          left: '50%', 
          top: 0, 
          transform: 'translateX(-50%)', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
        }}
      >
        {(editMode === 'edit' || editMode === 'presand' || editMode === 'breakdown' || editMode === 'dhGauge') ? <Typography variant="h6" color="#f44336">EDIT MODE</Typography> : 
        editMode === 'comment' ? <Typography variant="h6" color="green">COMMENT MODE</Typography> : ''}
      </div>}

      {(chartName === 'Pumping' || chartName === 'Pressure') && editMode && <div 
        style={{
          zIndex: 69, 
          position: 'absolute', 
          left: '50%', 
          bottom: 0, 
          transform: 'translateX(-50%)', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
        }}
      >
        {editMode === 'edit' ? <Typography variant="body2" color="#f44336">Click to add or remove rate drop points</Typography> : 
        editMode === 'comment' ? <Typography variant="body2" color="green">Click to add or remove comments</Typography> :
        editMode === 'presand' ? <Typography variant="body2" color="#f44336">Click to set pre sand line</Typography> : 
        editMode === 'breakdown' ? <Typography variant="body2" color="#f44336">Click to set fracture breakdown line</Typography> : 
        ''}
      </div>}

      {chartName === 'Historic' && <div style={{position: 'absolute', top: 5, left: 10}}>
        <Typography variant="caption" style={{marginRight: 10}}>Filter:</Typography>
        <FormControl>
          <Select 
            value={selectedStages}
            label="Stages"
            title="Select Historic Stages"
            multiple
            onChange={handleStagesChange}
            size="small"
            variant="standard"
            style={{width: 150, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
            renderValue={(selected) => selected.join(', ')}
          >
            <MenuItem value='Select All'>
              <Checkbox checked={true} />
              <ListItemText primary='Select All' />
            </MenuItem>
            <MenuItem value='Deselect All'>
              <Checkbox checked={false} />
              <ListItemText primary='Deselect All' />
            </MenuItem>
            {historicStages?.map((name,i) => (
              <MenuItem key={`${name}-${i}`} value={name} disabled={name === currentStageName}>
                <Checkbox checked={selectedStages.indexOf(name) > -1 || name === currentStageName} />
                <ListItemText primary={name} style={{color: darkMode ? 'white' : currentRTFO?.stageData?.[name]?.coldboreData ? "#333" : "lightgrey"}} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Typography variant="caption" style={{marginRight: 10}}>Traces:</Typography>
        <FormControl>
          <Select 
            value={historicTraces}
            label="Traces"
            title="Select Traces"
            multiple
            onChange={handleHistoricTracesChange}
            size="small"
            variant="standard"
            style={{width: 100, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
            renderValue={(selected) => selected.map(val => formatTraceName(val)).join(', ')}
          >
            {['pressure', 'rate', 'fracfeed', 'mfr', 'proppant', 'fr']?.map((name,i) => (
              <MenuItem key={`${name}-${i}`} value={name}>
                <Checkbox checked={historicTraces.indexOf(name) > -1} />
                <ListItemText primary={ formatTraceName(name) } />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="caption" style={{marginRight: 10}}>Norm:</Typography>
        <FormControl>
          <Select 
            value={historicChartNormalizeBy}
            label="Normalize By"
            title="Normalize By"
            onChange={(e) => setHistoricChartNormalizeBy(e.target.value)}
            size="small"
            variant="standard"
            style={{width: 75, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
          >
            {['proppant', 'volume'].map((normalizeBy) => {
              return (
                <MenuItem value={normalizeBy} key={normalizeBy}>{normalizeBy === 'proppant' ? 'Proppant' : 'Volume'}</MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Typography variant="caption" style={{marginRight: 10}}>Axis:</Typography>
        <FormControl>
          <Select 
            value={historicChartType}
            label="Chart Type"
            title="Chart Type"
            onChange={(e) => setHistoricChartType(e.target.value)}
            size="small"
            variant="standard"
            style={{width: 75, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
          >
            {['time', 'cumVolume', 'cumProppant'].map((chart) => {
              return (
                <MenuItem value={chart} key={chart}>{chart === 'time' ? 'Time' : chart === 'cumVolume' ? 'Volume' : 'Proppant'}</MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>}

      {chartName === 'ISIP' && <div style={{position: 'absolute', top: 5, left: 100}}>
        <Typography variant="caption" style={{marginRight: 10}}>Filter:</Typography>
        <FormControl>
          <Select 
            value={selectedStages}
            label="Stages"
            title="Select Historic Stages"
            multiple
            onChange={handleStagesChange}
            size="small"
            variant="standard"
            style={{width: 150, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
            renderValue={(selected) => selected.join(', ')}
          >
            <MenuItem value='Select All'>
              <Checkbox checked={true} />
              <ListItemText primary='Select All' />
            </MenuItem>
            <MenuItem value='Deselect All'>
              <Checkbox checked={false} />
              <ListItemText primary='Deselect All' />
            </MenuItem>
            {historicStages?.map((name,i) => (
              <MenuItem key={`${name}-${i}`} value={name} disabled={name === currentStageName}>
                <Checkbox checked={selectedStages.indexOf(name) > -1 || name === currentStageName} />
                <ListItemText primary={name} style={{color: currentRTFO?.stageData?.[name]?.coldboreData ? "#333" : "lightgrey"}} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>}

      {chartName === 'Pumpdown History' && <div style={{position: 'absolute', top: 5, left: 10}}>
        <Typography variant="caption" style={{marginRight: 10}}>Filter:</Typography>
        <FormControl>
          <Select 
            value={selectedStages}
            label="Stages"
            title="Select Historic Stages"
            multiple
            onChange={handleStagesChange}
            size="small"
            variant="standard"
            style={{width: 150, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
            renderValue={(selected) => selected.join(', ')}
          >
            <MenuItem value='Select All'>
              <Checkbox checked={true} />
              <ListItemText primary='Select All' />
            </MenuItem>
            <MenuItem value='Deselect All'>
              <Checkbox checked={false} />
              <ListItemText primary='Deselect All' />
            </MenuItem>
            {historicStages?.map((name,i) => (
              <MenuItem key={`${name}-${i}`} value={name} disabled={name === currentStageName}>
                <Checkbox checked={selectedStages.indexOf(name) > -1 || name === currentStageName} />
                <ListItemText primary={name} style={{color: currentRTFO?.stageData?.[name]?.coldboreData ? "#333" : "lightgrey"}} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Typography variant="caption" style={{marginRight: 10}}>Traces:</Typography>
        <FormControl>
          <Select 
            value={pumpdownHistoryTraces}
            label="Traces"
            title="Select Traces"
            multiple
            onChange={handlePumpdownHistoryTracesChange}
            size="small"
            variant="standard"
            style={{width: 100, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
            renderValue={(selected) => selected.map(val => formatTraceName(val)).join(', ')}
          >
            {['pressure', 'rate', 'depth']?.map((name,i) => (
              <MenuItem key={`${name}-${i}`} value={name}>
                <Checkbox checked={pumpdownHistoryTraces.indexOf(name) > -1} />
                <ListItemText primary={ formatTraceName(name) } />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="caption" style={{marginRight: 10}}>Norm:</Typography>
        <FormControl>
          <Select 
            value={pumpdownHistoryNormalizeBy}
            label="Normalize By"
            title="Normalize By"
            onChange={(e) => setPumpdownHistoryNormalizeBy(e.target.value)}
            size="small"
            variant="standard"
            style={{width: 75, height: 28, zIndex: 69, fontSize: '12px', marginRight: 20}}
          >
            {['depth', 'postPlug'].map((normalizeBy) => {
              return (
                <MenuItem value={normalizeBy} key={normalizeBy}>{normalizeBy === 'depth' ? 'Depth' : 'Post Plug'}</MenuItem>
              );
            })}
          </Select>
        </FormControl>

      </div>}

      {!editMode && !seismicEditMode && <FormControl style={{position: 'absolute', top: 5, right: 10}}>
        <Select 
          value={chartName}
          label="Chart"
          title="Select Chart"
          onChange={(e) => setChartName(e.target.value)}
          size="small"
          variant="standard"
          style={{width: 100, height: 28, zIndex: 69, fontSize: '12px'}}
        >
          {
            (currentRTFO?.client === 'marathon' ? 
              ['Pumping', 'Pressure', 'Historic', 'Derivative', 'Monitor', 'Stepdown', 'ISIP', 'Pumpdown', 'Pumpdown History', 'Fracfeed Simulator', 'None'] :
              ['Pumping', 'Pressure', 'Historic', 'Derivative', 'Monitor', 'Stepdown', 'ISIP', 'Seismic', 'Pumpdown', 'Pumpdown History', 'Fracfeed Simulator', 'None']
            )?.map((chart) => {
            return (
              <MenuItem value={chart} key={chart}>{chart}</MenuItem>
            );
          })}
        </Select>
      </FormControl>}

      {(editMode || seismicEditMode) && (chartName === 'Pumping' || chartName === 'Pressure' || chartName === 'Seismic') &&  
        <ToggleButtonGroup
          value={''}
          size="small"
          style={{height: 24, position: 'absolute', top: 10, right: 10}}
          onChange={() => {}}
        >
          <ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges} >
            <Cancel style={{fontSize: '16px'}} />
          </ToggleButton>
          <ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={handleSaveChanges}>
            <Save style={{fontSize: '16px'}} />
          </ToggleButton>
        </ToggleButtonGroup>}
      
      {stageData?.coldboreData ? renderChart(
        chartName, 
        selectedStages, 
        chartOverlays?.includes('spikelines'), 
        chartOverlays?.includes('legend'),
        chartOverlays?.includes('comments'),
        stageData,
        historicTraces,
        pumpdownHistoryTraces,
        historicChartType,
        historicChartNormalizeBy,
        pumpdownHistoryNormalizeBy,
        setSelectedComment,
        setEditCommentVisible,
        editMode,
        breakdownTimestamp,
        breakdownPressure,
        setBreakdownTimestamp,
        setBreakdownPressure,
        seismicEditMode,
        seismicAmplitudeEvents,
        setSeismicAmplitudeEvents,
        seismicDataType,
        chartTemplates,
        selectedTemplate,
        darkMode
      ) : 
      currentRTFO?.isLoadingData ? 
      <div>
        <CircularProgress
          size={24}
        />
      </div> : 
      <Typography variant="body2">No data selected</Typography>}

      {editCommentVisible && <RTFOEditComment 
        stageData={stageData}
        editMode={editMode}
        setEditMode={setEditMode}
        darkMode={darkMode} 
        editCommentVisible={editCommentVisible}
        setEditCommentVisible={setEditCommentVisible} 
        selectedComment={selectedComment} 
        setSelectedComment={setSelectedComment}
        stageComments={stageComments}
        setStageComments={setStageComments}
      />}

    </div>
  );
              
  function renderChart(
    chartName, 
    selectedStages, 
    showSpikeLines, 
    showLegend,
    showComments,
    stageData,
    historicTraces,
    pumpdownHistoryTraces,
    historicChartType,
    historicChartNormalizeBy,
    pumpdownHistoryNormalizeBy,
    setSelectedComment,
    setEditCommentVisible,
    editMode,
    breakdownTimestamp,
    breakdownPressure,
    setBreakdownTimestamp,
    setBreakdownPressure,
    seismicEditMode,
    seismicAmplitudeEvents,
    setSeismicAmplitudeEvents,
    seismicDataType,
    chartTemplates,
    selectedTemplate,
    darkMode
  ) {

    switch(chartName) {
      case 'Pumping':
        return <RTFOPumpingChart 
          gridView={gridView} 
          showSpikeLines={showSpikeLines} 
          showLegend={showLegend} 
          showComments={showComments}
          events={stageData?.events || {}}
          stageComments={stageData?.comments}
          setSelectedComment={setSelectedComment}
          setEditCommentVisible={setEditCommentVisible}
          preSandTimestamp={stageData?.metrics?.preSandTimestamp}
          editMode={editMode}
          isEditMode={editMode === 'breakdown'}
          isCommenttMode={editMode === 'comment'}
          breakdownPressure={breakdownPressure}
          breakdownTimestamp={breakdownTimestamp}
          setBreakdownTimestamp={setBreakdownTimestamp}
          setBreakdownPressure={setBreakdownPressure}
        />;
      case 'Pressure':
        return <RTFOPressureChart 
          gridView={gridView} 
          showSpikeLines={showSpikeLines} 
          showLegend={showLegend} 
          showComments={showComments}
          events={stageData?.events || {}}
          stageComments={stageData?.comments}
          setSelectedComment={setSelectedComment}
          setEditCommentVisible={setEditCommentVisible}
          preSandTimestamp={stageData?.metrics?.preSandTimestamp}
          editMode={editMode}
          isEditMode={editMode === 'breakdown'}
          isCommentMode={editMode === 'comment'}
          breakdownTimestamp={breakdownTimestamp}
          setBreakdownTimestamp={setBreakdownTimestamp}
          setBreakdownPressure={setBreakdownPressure}
          chartTemplate={chartTemplates?.[selectedTemplate]}
        />;
      case 'Historic':
        return <RTFOHistoricChart gridView={gridView} wellName={currentWellName} selectedStages={selectedStages} historicTraces={historicTraces} chartType={historicChartType} normalizeBy={historicChartNormalizeBy} />;
      case 'Derivative':
        return <RTFODerivativeChart 
          gridView={gridView} 
          showSpikeLines={showSpikeLines} 
          events={stageData?.events || {}}
          showLegend={showLegend} />;
      case 'Monitor':
        return <RTFOMonitorChart gridView={gridView} showSpikeLines={showSpikeLines} showLegend={showLegend} />;
      case 'Stepdown':
        if (stageData?.stepdownData && stageData?.stepdownData?.length > 0 && stageData?.stepdownParams && Object.keys(stageData?.stepdownParams)?.length > 0) {
          return (
            <RTFOStepdownChart 
              stepdownData={stageData?.stepdownData} 
              gridView={gridView} 
              currentRTFO={currentRTFO}
              darkMode={darkMode}
              showSpikeLines={showSpikeLines} 
              showLegend={showLegend} 
              sdtType={'flush'}
            />
          );
        } else if (stageData?.metrics?.isip > 0 && (!stageData?.stepdownData || stageData?.stepdownData?.length === 0 || !stageData?.stepdownParams || Object.keys(stageData?.stepdownParams)?.length === 0)) {
          return (
            <div style={{
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Analytics style={{fontSize: '72px', color: 'grey'}} />
              <Typography variant="body2" style={{color: 'grey'}}>Stepdown not available</Typography>
            </div>
          )
        } else {
          return (
            <div style={{
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute', 
              height: 200,
              width: 400,
              top: '50%', 
              left: '50%', 
              transform: 'translate(-200px, -100px)'
            }}>
              <CircularProgress size={40} style={{marginBottom: 20}} />
              <Typography variant="body2">Awaiting stepdown data...</Typography>
            </div>
          );
        };
      case 'ISIP':
        return (
          <RTFOISIPChart gridView={gridView} wellName={currentWellName} showSpikeLines={showSpikeLines} showLegend={showLegend} selectedStages={selectedStages} />
        );
      case 'Seismic':
        return (
          <div style={{display: 'flex', height: '100%', width: '100%', flexDirection: 'column'}}>
            <div style={{height: gridView ? 540 : 900, width: '100%', overflow: 'scroll'}}>
              <RTFOSeismicAmplitudeChart 
                gridView={gridView} 
                showSpikeLines={showSpikeLines} 
                showLegend={showLegend} 
                seismicEditMode={seismicEditMode}
                seismicAmplitudeEvents={seismicAmplitudeEvents}
                setSeismicAmplitudeEvents={setSeismicAmplitudeEvents}
                seismicDataType={seismicDataType}
              />
            </div>

            <div style={{height: 500, width: '100%', overflow: 'scroll'}}>
              <RTFOSeismicAmplitudeTable currentStageName={currentStageName} seismicAmplitudeEvents={seismicAmplitudeEvents} darkMode={darkMode} />
            </div>
          </div>
        );
      case 'Pumpdown':
        return (
          <RTFOPumpdownChart
            gridView={gridView} 
            showSpikeLines={showSpikeLines} 
            pumpdownData={pumpdownData || []}
            pumpdownFalloffTimestamps={stageData?.pumpdownParams?.pumpdownFalloffTimestamps}
            pressureTestTimestamps={stageData?.pumpdownParams?.pressureTestTimestamps}
            postPlugFalloffTimestamps={stageData?.pumpdownParams?.postPlugFalloffTimestamps}
            postPerfFalloffTimestamps={stageData?.pumpdownParams?.postPerfFalloffTimestamps}
            customTimestamps={stageData?.pumpdownParams?.customTimestamps}
          />
        );
      case 'Pumpdown History':
        return (
          <RTFOHistoricPumpdownChart gridView={gridView} selectedStages={selectedStages} pumpdownHistoryTraces={pumpdownHistoryTraces} pumpdownHistoryNormalizeBy={pumpdownHistoryNormalizeBy} />
        );
      case 'Fracfeed Simulator':
        return (
          <RTFOFracfeedSimulatorChart gridView={gridView} showSpikeLines={showSpikeLines} showLegend={showLegend} />
        );
      case 'None':
        return (<div style={{
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <InsertChart style={{fontSize: '72px', color: 'grey'}} />
          <Typography variant="body2" style={{color: 'grey'}}>No chart selected</Typography>
        </div>)
      default:
        return (<div style={{
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <InsertChart style={{fontSize: '72px', color: 'grey'}} />
          <Typography variant="body2" style={{color: 'grey'}}>No chart selected</Typography>
        </div>)
    };
  };

  function handleCancelChanges() {
    try {
      
      setEditMode(false);
      setSeismicEditMode(false);
      setBreakdownTimestamp(stageData?.metrics?.breakdownTimestamp || null);
      setBreakdownPressure(stageData?.metrics?.breakdownPressure || 0);
      let allAmplitudeEvents = [];
      Object.keys(currentRTFO?.stageData)?.forEach(stageName => {
        if (currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents) {
          allAmplitudeEvents.push(currentRTFO?.stageData?.[stageName]?.seismicAmplitudeEvents);
        };
      });

      allAmplitudeEvents = allAmplitudeEvents.flat(Infinity);
      allAmplitudeEvents = allAmplitudeEvents.sort((a,b) => a.timestamp - b.timestamp);

      setSeismicAmplitudeEvents(allAmplitudeEvents);

    } catch (error) {
      console.log(error);
      return;
    };
  };
  function handleSaveChanges() {
    try {

      const stageSeismicAmplitudeEvents = seismicAmplitudeEvents?.filter(event => event?.stageName === stageData?.name) || [];
      socket?.send(JSON.stringify({
        messageType: 'saveStage',
        stage: {...stageData, metrics: {...stageData?.metrics, breakdownTimestamp, breakdownPressure}, seismicAmplitudeEvents: stageSeismicAmplitudeEvents}
      }));

      setEditMode(null);
      setSeismicEditMode(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };
  
  function handleKeyDown(event) {
    try {
      
      if (event.keyCode === 27) { // escape button
        handleCancelChanges();
      };
  
    } catch (error) {
      console.log(error);
      return;
    };
  };
};

function calculateTotalStages(perfSchedule) {
  try {

    if (!perfSchedule || !perfSchedule.wells) return 0;

    let totalPlannedStages = 0;

    for (const well of Object.keys(perfSchedule.wells)) {
      const wellStages = Object.keys(perfSchedule.wells[well]?.stages)?.length || 0;
      totalPlannedStages += wellStages;
    };

    return totalPlannedStages;

  } catch(error) {
    console.log(error);
    return 0;
  };
};

